/**
 * Slug mapping for multilingual blog posts
 * Dynamically loads locale-specific mappings to reduce bundle size
 */

// STATIC IMPORTS for SSR - these are bundled so they're available synchronously
// This is required because useEffect doesn't run during SSR, so we need
// synchronous slug resolution for proper server-side rendering
import enMappingStatic from './slugMappings/en.json';
import deMappingStatic from './slugMappings/de.json';
import ptBRMappingStatic from './slugMappings/pt-BR.json';
import msMYMappingStatic from './slugMappings/ms-MY.json';
import esMappingStatic from './slugMappings/es.json';
import frMappingStatic from './slugMappings/fr.json';
import jaMappingStatic from './slugMappings/ja.json';
import idMappingStatic from './slugMappings/id.json';

// Pre-built static mappings for synchronous access
const staticMappings: Record<string, Record<string, string>> = {
  'en': enMappingStatic,
  'de': deMappingStatic,
  'pt-BR': ptBRMappingStatic,
  'ms-MY': msMYMappingStatic,
  'es': esMappingStatic,
  'fr': frMappingStatic,
  'ja': jaMappingStatic,
  'id': idMappingStatic,
};

// Cache for loaded mappings to avoid repeated imports
const mappingCache: Record<string, Record<string, string>> = {};

/**
 * Dynamically load slug mapping for a specific locale
 * Uses dynamic import to code-split mappings by language (75% bundle reduction)
 */
async function loadSlugMapping(locale: string): Promise<Record<string, string>> {
  // Return cached mapping if available
  if (mappingCache[locale]) {
    return mappingCache[locale];
  }

  try {
    // Dynamic import based on locale (Vite will code-split these)
    let mapping: Record<string, string>;

    switch (locale) {
      case 'de':
        mapping = (await import('./slugMappings/de.json')).default;
        break;
      case 'pt-BR':
        mapping = (await import('./slugMappings/pt-BR.json')).default;
        break;
      case 'ms-MY':
        mapping = (await import('./slugMappings/ms-MY.json')).default;
        break;
      case 'es':
        mapping = (await import('./slugMappings/es.json')).default;
        break;
      case 'fr':
        mapping = (await import('./slugMappings/fr.json')).default;
        break;
      case 'ja':
        mapping = (await import('./slugMappings/ja.json')).default;
        break;
      case 'id':
        mapping = (await import('./slugMappings/id.json')).default;
        break;
      case 'en':
      default:
        mapping = (await import('./slugMappings/en.json')).default;
        break;
    }

    // Cache the loaded mapping
    mappingCache[locale] = mapping;
    return mapping;
  } catch (error) {
    console.error(`[SlugMapping] Failed to load mapping for locale "${locale}":`, error);
    // Fallback to English if loading fails
    if (locale !== 'en' && !mappingCache['en']) {
      mappingCache['en'] = (await import('./slugMappings/en.json')).default;
    }
    return mappingCache['en'] || {};
  }
}

/**
 * Get the localized slug for a given English slug and locale
 */
export async function getLocalizedSlug(englishSlug: string, locale: string): Promise<string> {
  const mapping = await loadSlugMapping(locale);
  return mapping[englishSlug] || englishSlug;
}

/**
 * Get the English slug from a localized slug
 */
export async function getEnglishSlug(localizedSlug: string, locale: string): Promise<string> {
  if (locale === 'en') return localizedSlug;

  const mapping = await loadSlugMapping(locale);

  // Reverse lookup
  for (const [englishSlug, localized] of Object.entries(mapping)) {
    if (localized === localizedSlug) {
      return englishSlug;
    }
  }

  return localizedSlug;
}

/**
 * Preload slug mapping for a locale (optional optimization)
 * Call this when user hovers over a blog link to reduce perceived latency
 */
export function preloadSlugMapping(locale: string): void {
  // Fire and forget - don't await
  loadSlugMapping(locale).catch(() => {
    // Silently fail - not critical
  });
}

// =============================================================================
// SYNCHRONOUS FUNCTIONS FOR SSR
// These use statically imported mappings so they work during server-side rendering
// useEffect doesn't run on the server, so async functions can't be used for initial state
// =============================================================================

/**
 * SYNCHRONOUS: Get the English slug from a localized slug
 * Uses statically imported mappings - safe for SSR and initial React state
 */
export function getEnglishSlugSync(localizedSlug: string, locale: string): string {
  if (locale === 'en') return localizedSlug;

  const mapping = staticMappings[locale] || staticMappings['en'];

  // Reverse lookup: find the English key that maps to this localized value
  for (const [englishSlug, localized] of Object.entries(mapping)) {
    if (localized === localizedSlug) {
      return englishSlug;
    }
  }

  // Not found in mapping - return as-is (might be an English slug on non-English locale)
  return localizedSlug;
}

/**
 * SYNCHRONOUS: Get the localized slug for a given English slug
 * Uses statically imported mappings - safe for SSR and initial React state
 */
export function getLocalizedSlugSync(englishSlug: string, locale: string): string {
  const mapping = staticMappings[locale] || staticMappings['en'];
  return mapping[englishSlug] || englishSlug;
}
